import { useEffect, useRef, useState } from 'react';
import type { LifeAreaDTO, ObligationDTO } from '@almanac/shared';
import {
  formatRecurrenceEnd,
  formatTimeForDisplay,
  isAcknowledgedToday,
  isOccasionSuggestionHandled,
  isOverdue,
  liveObligationTitle,
  occasionOccurrenceKey,
  occasionReminderDraft,
  occasionSuggestionDue,
  splitRecurrenceEnd,
  useCompleteObligation,
  useCreateObligation,
  useDeleteObligation,
  useNowMinute,
  useReopenObligation,
  useUpdateObligation,
} from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { useUndoToast } from '../lib/undoToast.js';

interface Props {
  obligation: ObligationDTO;
  lifeArea?: LifeAreaDTO;
  /** Full life-areas list, used by the inline category picker.
   *  The single `lifeArea` above identifies the obligation's current
   *  area for display; this array drives the chips when the user
   *  taps the category row to change it. */
  lifeAreas: LifeAreaDTO[];
  onClose: () => void;
  /** Called when the Edit action is taken. The page closes this
   *  preview first, then opens the full edit drawer for the same id —
   *  see `Obligations.tsx` / `Today.tsx` for the close-then-open
   *  routing. */
  onEdit: () => void;
}

/**
 * Read-first preview of an obligation. Opens as a centered card with
 * a backdrop scrim, distinct from the existing EditDrawer (which is a
 * bottom sheet) so the two read as different surfaces — quick-look
 * vs. full edit.
 *
 * Most fields are read-only summaries (date prose, recurrence prose,
 * the priority badge, created and last-completed timestamps). Notes
 * are the exception: the field is an inline textarea that saves on
 * blur, so quick "I should do X next time" jots don't need a trip
 * through the full edit drawer.
 *
 * Actions live in the footer:
 *
 *  - **Mark complete / Acknowledge / Reopen** — same behaviour as the
 *    row's checkbox, with the same undo toast. Closes the preview.
 *  - **Edit** — close-then-open: dismisses the preview and the page
 *    opens the EditDrawer in its place.
 *
 * Snooze chips sit in their own block above the actions and are only
 * shown for active tasks. Occasions don't snooze (a birthday is on a
 * specific date) and completed rows don't either (they're done).
 * Snooze updates `nextDueDate` only, so a recurring task's anchor
 * `dueDate` stays put and the schedule continues to fire on the
 * original cadence.
 */
export function ObligationPreview({ obligation, lifeArea, lifeAreas, onClose, onEdit }: Props) {
  const complete = useCompleteObligation();
  const reopen = useReopenObligation();
  const update = useUpdateObligation();
  const undo = useUndoToast();
  const people = usePeople();
  // Minute-tick so the Due fact flips to the overdue treatment the
  // moment a timed `today HH:MM` crosses, without the user having
  // to close and reopen the card.
  const now = useNowMinute();

  const isCompleted = obligation.status === 'completed';
  const isOccasion = obligation.kind === 'occasion';
  const acknowledgedToday = !isCompleted && isAcknowledgedToday(obligation);
  const title = liveObligationTitle(obligation, people.data);
  const verb = isOccasion ? 'acknowledged' : 'complete';
  const isShared = obligation.visibility === 'shared';

  // Local UI state for the inline category picker. Tapping the
  // Category row expands a chip list right below the row; selecting
  // a chip applies the change immediately (optimistic via update()
  // mutation) and re-collapses. State doesn't persist across opens
  // of the preview card — each new open starts collapsed.
  const [categoryOpen, setCategoryOpen] = useState(false);

  // Notes draft is held locally so each keystroke doesn't fire a
  // mutation; the value flushes on blur (and any path that closes
  // the card runs `commitNotes()` first so a tap on Close / Edit /
  // Snooze / scrim doesn't lose what the user was typing).
  const [notesDraft, setNotesDraft] = useState(obligation.notes ?? '');
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotesDraft(obligation.notes ?? '');
  }, [obligation.id, obligation.notes]);

  // Auto-resize the notes textarea to fit its content. Cheaper than a
  // dedicated react package and avoids the textarea showing a
  // scrollbar inside the card. The card itself caps at 85vh and
  // scrolls when notes get genuinely long.
  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [notesDraft]);

  function commitNotes() {
    const next = notesDraft.trim();
    const current = (obligation.notes ?? '').trim();
    if (next === current) return;
    // Empty notes are stored as null so they round-trip cleanly with
    // the existing "no notes" rendering elsewhere in the app.
    update.mutate({ id: obligation.id, input: { notes: next || null } });
  }

  // Single funnel for every dismiss path so we never miss the save.
  // The browser's own blur event will *usually* fire before the
  // click handler runs, but iOS is fussy about ordering when the
  // user taps the scrim with the keyboard still up; calling
  // commitNotes() explicitly makes the save deterministic.
  function closeWithSave() {
    commitNotes();
    onClose();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeWithSave();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // closeWithSave reads the latest notesDraft via closure on every
    // render; we deliberately re-bind the listener so Escape always
    // saves whatever's currently typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, obligation.id]);

  function handleComplete() {
    commitNotes();
    if (isCompleted) {
      reopen.mutate({ id: obligation.id, nextDueDate: obligation.dueDate });
      onClose();
      return;
    }
    const previousNextDueDate = obligation.nextDueDate ?? obligation.dueDate;
    const id = obligation.id;
    complete.mutate({ id });
    undo.show(`Marked “${title}” ${verb}`, () => {
      reopen.mutate({ id, nextDueDate: previousNextDueDate });
    });
    onClose();
  }

  function snoozeTo(iso: string) {
    commitNotes();
    // Only nextDueDate moves on snooze — the anchor dueDate is the
    // historical reference and a recurring rule keeps firing off it.
    // For a one-off task nextDueDate may have been null; setting it
    // explicitly makes the row pick up the new effective date via
    // the existing `nextDueDate ?? dueDate` fallback.
    update.mutate({ id: obligation.id, input: { nextDueDate: iso } });
    onClose();
  }

  function handleEdit() {
    commitNotes();
    onEdit();
  }

  function commitTime(next: string | null) {
    if ((next ?? null) === (obligation.dueTime ?? null)) return;
    update.mutate({ id: obligation.id, input: { dueTime: next } });
  }

  function commitCategory(lifeAreaId: string) {
    if (lifeAreaId === obligation.lifeAreaId) {
      setCategoryOpen(false);
      return;
    }
    update.mutate({ id: obligation.id, input: { lifeAreaId } });
    setCategoryOpen(false);
  }

  // ── Occasion → task suggestion ────────────────────────
  // A birthday/anniversary within the horizon invites a one-tap
  // reminder task — same follow-through pattern as an expiring
  // document's "Add renewal task". Dismissals are pinned to this
  // occurrence, so next year's rollover re-arms the prompt.
  const createObligation = useCreateObligation();
  const deleteObligation = useDeleteObligation();
  const suggestionDue = occasionSuggestionDue(obligation, new Date(now));
  const suggestionHandled = isOccasionSuggestionHandled(obligation);
  const isBirthday = obligation.occasionType !== 'anniversary';

  function dismissSuggestion() {
    const key = occasionOccurrenceKey(obligation);
    update.mutate({ id: obligation.id, input: { suggestionDismissedFor: key } });
    undo.show('Suggestion dismissed', () => {
      update.mutate({ id: obligation.id, input: { suggestionDismissedFor: null } });
    });
  }

  function suggestAgain() {
    const key = occasionOccurrenceKey(obligation);
    update.mutate({ id: obligation.id, input: { suggestionDismissedFor: null } });
    undo.show('Suggestion back on', () => {
      update.mutate({ id: obligation.id, input: { suggestionDismissedFor: key } });
    });
  }

  function addReminderTask() {
    const key = occasionOccurrenceKey(obligation);
    const draft = occasionReminderDraft(obligation, title);
    createObligation.mutate(draft);
    update.mutate({ id: obligation.id, input: { suggestionDismissedFor: key } });
    undo.show(`Added “${draft.title}”`, () => {
      deleteObligation.mutate({ id: draft.id });
      update.mutate({ id: obligation.id, input: { suggestionDismissedFor: null } });
    });
  }

  const showSnooze = !isOccasion && !isCompleted && !acknowledgedToday;
  // The complete action gets a verb suited to the row's state. For
  // occasions whose last acknowledgment was today we skip the action
  // entirely — the row is "done for now", the user came here to read.
  const completeLabel = isCompleted
    ? 'Reopen'
    : isOccasion
      ? 'Acknowledge'
      : 'Mark complete';
  const showCompleteAction = !acknowledgedToday;

  return (
    <>
      <div className="preview-scrim" onClick={closeWithSave} aria-hidden />
      <div
        className="preview-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Details for ${title}`}
      >
        <button
          type="button"
          className="preview-card__close"
          onClick={closeWithSave}
          aria-label="Close"
        >
          ×
        </button>

        <div className="preview-card__header">
          <h2 className="preview-card__title">{title}</h2>
          <div className="preview-card__subtitle">
            {isOccasion
              ? formatOccasionSubtitle(obligation)
              : formatTaskSubtitle(obligation, lifeArea)}
            {isShared && (
              <>
                {' '}
                <span className="shared-pill">Shared</span>
              </>
            )}
          </div>
        </div>

        <div className="preview-card__facts">
          <FactLine
            label="Due"
            value={formatDueProse(obligation)}
            emphasize={isOverdue(obligation, now)}
          />
          {obligation.recurrenceRule && (
            <FactLine label="Repeats" value={formatRecurrenceProse(obligation.recurrenceRule)} />
          )}
          {!isOccasion && obligation.priority === 'high' && !isCompleted && (
            <FactLine label="Priority" value="High" emphasize />
          )}
          {/* Time row is hidden for occasions (always all-day by
              convention) and for completed rows (no point editing
              the time on something that's done). For everything
              else it's always visible — placeholder when unset
              gives the user a discoverable affordance to add one. */}
          {!isOccasion && !isCompleted && (
            <TimeRow
              value={obligation.dueTime}
              onChange={commitTime}
            />
          )}
          {/* Category row is task-only. Occasions are always under
              Personal by convention and the subtitle already shows
              their occasion type. Completed rows skip too — moving
              a completed task between categories is unusual enough
              to leave for the full edit drawer. */}
          {!isOccasion && !isCompleted && (
            <CategoryRow
              currentId={obligation.lifeAreaId}
              currentName={lifeArea?.name ?? 'Personal'}
              options={lifeAreas}
              open={categoryOpen}
              onToggle={() => setCategoryOpen((o) => !o)}
              onSelect={commitCategory}
            />
          )}
        </div>

        {suggestionDue && (
          suggestionHandled ? (
            <div className="preview-expiry preview-expiry--handled">
              <span className="preview-expiry__text">
                Noted — no reminder for this occasion.
              </span>
              <span className="preview-expiry__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={suggestAgain}
                >
                  Suggest again
                </button>
              </span>
            </div>
          ) : (
            <div className="preview-expiry">
              <span className="preview-expiry__text">
                {isBirthday ? 'This birthday is coming up.' : 'This anniversary is coming up.'}
              </span>
              <span className="preview-expiry__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={dismissSuggestion}
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={addReminderTask}
                >
                  {isBirthday ? 'Add gift reminder' : 'Add a reminder'}
                </button>
              </span>
            </div>
          )
        )}

        <textarea
          ref={notesRef}
          className="preview-card__notes preview-card__notes--editable"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={commitNotes}
          placeholder={
            isOccasion
              ? 'Gift ideas, what you did last year, anything that helps next time…'
              : 'Add a note…'
          }
          rows={1}
          aria-label="Notes"
        />

        <div className="preview-card__timestamps">
          <span>Created {formatStamp(obligation.createdAt)}</span>
          {obligation.completedAt && (
            <span>
              Last {isOccasion ? 'acknowledged' : 'completed'}{' '}
              {formatStamp(obligation.completedAt)}
            </span>
          )}
        </div>

        {showSnooze && (
          <div className="preview-card__snooze">
            <div className="preview-card__section-label">Snooze</div>
            <div className="preview-card__chips">
              <button
                type="button"
                className="preview-card__chip"
                onClick={() => snoozeTo(offsetIso(1))}
              >
                Tomorrow
              </button>
              <button
                type="button"
                className="preview-card__chip"
                onClick={() => snoozeTo(offsetIso(7))}
              >
                Next week
              </button>
            </div>
          </div>
        )}

        <footer className="preview-card__footer">
          <button
            type="button"
            className="btn btn--secondary btn--small"
            onClick={handleEdit}
          >
            Edit
          </button>
          {showCompleteAction && (
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={handleComplete}
            >
              {completeLabel}
            </button>
          )}
        </footer>
      </div>
    </>
  );
}

function FactLine({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="preview-card__fact">
      <span className="preview-card__fact-label">{label}</span>
      <span
        className={`preview-card__fact-value ${emphasize ? 'preview-card__fact-value--emphasize' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Inline time editor that sits in the preview card's facts block.
 * The value text is the readable display (`3:00 PM` / `All day`); a
 * hidden, full-bleed native time input behind it opens the OS picker
 * on tap and writes back through `onChange`. Mirrors the "Pick date…"
 * pattern from WhenPill — same reasoning, same trick.
 *
 * When a time is set, a small ✕ surfaces alongside the value to
 * clear back to all-day without having to fight the OS picker's
 * own clear affordance (some platforms don't expose one).
 */
function TimeRow({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const display = value ? formatTimeForDisplay(value) : 'Set time';
  return (
    <div className="preview-card__fact preview-card__fact--editable">
      <span className="preview-card__fact-label">Time</span>
      <label
        className={`preview-card__fact-value preview-card__fact-value--editable ${value ? '' : 'preview-card__fact-value--placeholder'}`}
        onClick={() => {
          const input = inputRef.current;
          if (input && typeof input.showPicker === 'function') {
            try {
              input.showPicker();
            } catch {
              // Non-user-gesture invocations throw; harmless.
            }
          }
        }}
      >
        {display}
        <input
          ref={inputRef}
          type="time"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="preview-card__time-input"
          aria-label="Time of day"
        />
      </label>
      {value && (
        <button
          type="button"
          className="preview-card__fact-clear"
          onClick={() => onChange(null)}
          aria-label="Clear time"
        >
          ×
        </button>
      )}
    </div>
  );
}

/**
 * Inline category picker. Tapping the value expands a row of chips
 * underneath the fact line; selecting a chip applies the change and
 * collapses. The chips inherit the popover-chip styling so the
 * visual language is consistent with the When pill's Repeats group.
 */
function CategoryRow({
  currentId,
  currentName,
  options,
  open,
  onToggle,
  onSelect,
}: {
  currentId: string;
  currentName: string;
  options: LifeAreaDTO[];
  open: boolean;
  onToggle: () => void;
  onSelect: (id: string) => void;
}) {
  // Close the popover when the user taps anywhere outside the
  // category row's wrapping container (including elsewhere on the
  // card itself). Without this, opening the popover and then tapping
  // another row would leave the popover floating until the next
  // explicit toggle. Tap on the trigger button stays inside the
  // container so the button's own onClick handles the toggle.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (target && containerRef.current && !containerRef.current.contains(target)) {
        onToggle();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onToggle]);

  return (
    <div ref={containerRef} className="preview-card__category-row">
      <button
        type="button"
        className="preview-card__fact preview-card__fact--toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="preview-card__fact-label">Category</span>
        <span className="preview-card__fact-value">{currentName}</span>
        <span className="preview-card__fact-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div
          className="preview-card__chips preview-card__chips--popover"
          role="radiogroup"
          aria-label="Category"
        >
          {options.map((a) => (
            <button
              key={a.id}
              type="button"
              role="radio"
              aria-checked={a.id === currentId}
              className={`preview-card__chip ${a.id === currentId ? 'preview-card__chip--active' : ''}`}
              onClick={() => onSelect(a.id)}
            >
              {a.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function offsetIso(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatTaskSubtitle(o: ObligationDTO, lifeArea?: LifeAreaDTO): string {
  const parts: string[] = [lifeArea?.name ?? 'Personal'];
  if (o.recurrenceRule) parts.push('Recurring');
  return parts.join(' · ');
}

function formatOccasionSubtitle(o: ObligationDTO): string {
  const label =
    o.occasionType === 'anniversary'
      ? o.occasionLabel?.trim() || 'Anniversary'
      : 'Birthday';
  const nextYear = o.nextDueDate ? Number(o.nextDueDate.slice(0, 10).slice(0, 4)) : null;
  const years = nextYear && o.anchorYear ? nextYear - o.anchorYear : null;
  if (years === null || years <= 0) return label;
  if (o.occasionType === 'anniversary') {
    return `${label} · ${years} ${years === 1 ? 'year' : 'years'}`;
  }
  return `${label} · turns ${years}`;
}

function formatDueProse(o: ObligationDTO): string {
  if (o.status === 'completed') {
    return o.completedAt ? `Done ${formatStamp(o.completedAt)}` : 'Done';
  }
  const due = o.nextDueDate ?? o.dueDate;
  if (!due) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${due.slice(0, 10)}T00:00:00`);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const exact = target.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(target.getFullYear() !== today.getFullYear() ? { year: 'numeric' as const } : {}),
  });
  // Time joins the exact date with `at` ("Wednesday, 22 May at 3:00 PM")
  // when set — reads more naturally than "Wednesday, 22 May · 3:00 PM"
  // and matches the way the user would say it aloud.
  const exactWithTime = o.dueTime ? `${exact} at ${formatTimeForDisplay(o.dueTime)}` : exact;
  if (diff === 0) return `Today · ${exactWithTime}`;
  if (diff === 1) return `Tomorrow · ${exactWithTime}`;
  if (diff === -1) return `Yesterday · ${exactWithTime}`;
  if (diff > 1 && diff < 7) return `In ${diff} days · ${exactWithTime}`;
  if (diff < 0) return `${-diff} days overdue · ${exactWithTime}`;
  return exactWithTime;
}

function formatRecurrenceProse(rule: string): string {
  const { base, end } = splitRecurrenceEnd(rule);
  const cadence =
    base === 'FREQ=DAILY'
      ? 'Daily'
      : base === 'FREQ=WEEKLY'
        ? 'Weekly'
        : base === 'FREQ=MONTHLY'
          ? 'Monthly'
          : base.startsWith('FREQ=YEARLY')
            ? 'Yearly'
            : 'Custom schedule';
  const suffix = formatRecurrenceEnd(end);
  return suffix ? `${cadence} · ${suffix}` : cadence;
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
